import React, { ReactElement, useEffect, useRef, useState } from 'react';

import { useLocation } from '@reach/router';
import cx from 'classnames';

import { Grid, MenuButton, Navigation } from '../../components';
import { IAppContext } from '../../context/app/app.types';
import { LocomotiveScrollProvider } from '../../context/locomotiveScroll/locomotiveScroll.provider';
import { useAppContext } from '../../hooks/context/appContext.hook';
import { isScrollEnabled } from '../../utils/isScrollEnabled.util';
import { ILayoutProps } from './Layout.types';

export const Layout: React.FC<ILayoutProps> = ({
	children,
	className,
}): ReactElement => {
	const [isDark, setisDark] = useState<boolean>(false);
	const [hasSmoothScroll, setHasSmoothScroll] = useState<boolean>(true);

	const { pathname } = useLocation();

	const { isGridVisible, isGridEnabled, isCampaignVisible }: IAppContext =
		useAppContext();

	const scrollRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const scrollEnabled = pathname !== '/' && isScrollEnabled(); // index page
		const darkEnabled =
			pathname.includes('about') || // about pages
			pathname.includes('career') || // career pages
			pathname.includes('not-found') || // not found page
			(!pathname.includes('collections') && pathname.length > 1); // default pages

		setHasSmoothScroll(scrollEnabled);
		setisDark(darkEnabled);

		return (): void => {
			setHasSmoothScroll(true);
			setisDark(false);
		};
	}, [pathname]);

	useEffect(() => {
		if (!document) {
			return;
		}

		if (isGridEnabled && !document.body.classList.contains('has-grid-enabled')) {
			document.body.classList.add('has-grid-enabled');
		}

		if (!isGridEnabled && document.body.classList.contains('has-grid-enabled')) {
			document.body.classList.remove('has-grid-enabled');
		}
	}, [isGridEnabled]);

	return (
		<LocomotiveScrollProvider
			isEnabled={hasSmoothScroll}
			containerRef={scrollRef}
			options={{
				smooth: true,
				lerp: 0.05,
				multiplier: 0.5,
				tablet: { smooth: true },
				mobile: { smooth: true },
			}}
			watch={[pathname]}
			location={pathname}>
			<div className="c-container" data-scroll-container ref={scrollRef}>
				<Navigation />
				<div
					className={cx(
						pathname === '/'
							? 'u-fill-height-vh--alt'
							: 'u-fill-height-vh',
						'u-fill-width',
						!hasSmoothScroll && 'has-default-scroll',
						className,
					)}>
					{isGridVisible && (
						<div className="u-pos-absolute u-top-0 u-bottom-0 u-fill-width u-overflow-hidden">
							<Grid />
						</div>
					)}
					<div
						className={cx({
							'c-main__content': true,
							'u-pointer-events-none': isGridEnabled,
						})}>
						{children}
					</div>
				</div>
				{!isCampaignVisible && (
					<MenuButton isDark={isDark} className="u-pointer-events-none" />
				)}
			</div>
		</LocomotiveScrollProvider>
	);
};
